import type { ContinuousAuthEvent, ContinuousAuthStatus } from '@/types/auth';
import { apiClient } from './api-client';

type WSEventHandler = (event: ContinuousAuthEvent) => void;
type WSStatusHandler = (status: ContinuousAuthStatus) => void;

export interface WSClientConfig {
  /** WebSocket endpoint path */
  endpoint: string;
  /** Reconnection delay base in ms */
  reconnectBaseDelay: number;
  /** Max reconnection attempts */
  maxReconnectAttempts: number;
  /** Heartbeat interval in ms */
  heartbeatInterval: number;
}

const DEFAULT_CONFIG: WSClientConfig = {
  endpoint: '/ws/continuous-auth',
  reconnectBaseDelay: 1000,
  maxReconnectAttempts: 10,
  heartbeatInterval: 30000,
};

/**
 * WebSocket client for continuous authentication and live feedback.
 * Handles reconnection with exponential backoff, heartbeat, and
 * token-based authentication.
 */
export class WSClient {
  private config: WSClientConfig;
  private ws: WebSocket | null = null;
  private status: ContinuousAuthStatus = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private eventHandlers = new Set<WSEventHandler>();
  private statusHandlers = new Set<WSStatusHandler>();
  private manualClose = false;

  constructor(config: Partial<WSClientConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /** Subscribe to continuous auth events */
  onEvent(handler: WSEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  /** Subscribe to connection status changes */
  onStatus(handler: WSStatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  /** Get current connection status */
  getStatus(): ContinuousAuthStatus {
    return this.status;
  }

  /** Connect to the WebSocket server */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    this.manualClose = false;
    const token = apiClient.getAccessToken();
    if (!token) {
      this.setStatus('disconnected');
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${protocol}//${host}${this.config.endpoint}?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(url);
      this.setupEventListeners();
    } catch (error) {
      console.error('[WSClient] Connection error:', error);
      this.scheduleReconnect();
    }
  }

  /** Disconnect from the server */
  disconnect(): void {
    this.manualClose = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.setStatus('disconnected');
  }

  /** Send keystroke events for continuous auth evaluation */
  sendKeystrokeEvents(events: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      type: 'keystroke_events',
      payload: events,
      timestamp: Date.now(),
    }));
  }

  /** Send a heartbeat/ping */
  private sendHeartbeat(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
  }

  /** Setup WebSocket event listeners */
  private setupEventListeners(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.setStatus('active');
      this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as ContinuousAuthEvent;
        this.handleEvent(data);
      } catch (error) {
        console.error('[WSClient] Failed to parse message:', error);
      }
    };

    this.ws.onclose = (event) => {
      this.stopHeartbeat();
      if (!this.manualClose) {
        if (event.code === 4001) {
          // Auth expired
          this.setStatus('disconnected');
        } else {
          this.setStatus('degraded');
          this.scheduleReconnect();
        }
      }
    };

    this.ws.onerror = () => {
      this.setStatus('degraded');
    };
  }

  /** Handle incoming event */
  private handleEvent(event: ContinuousAuthEvent): void {
    // Update status based on event
    if (event.type === 'score_update') {
      if (event.zone === 'accept') {
        this.setStatus('active');
      } else if (event.zone === 'gray_zone') {
        this.setStatus('warning');
      } else if (event.zone === 'reject') {
        this.setStatus('degraded');
      }
    } else if (event.type === 'session_revoked') {
      this.setStatus('disconnected');
      this.disconnect();
    }

    // Notify handlers
    this.eventHandlers.forEach((handler) => handler(event));
  }

  /** Update and broadcast status */
  private setStatus(status: ContinuousAuthStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.statusHandlers.forEach((handler) => handler(status));
    }
  }

  /** Schedule a reconnection with exponential backoff */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.setStatus('disconnected');
      return;
    }

    const delay = this.config.reconnectBaseDelay * Math.pow(2, this.reconnectAttempts);
    const jitter = delay * 0.2 * Math.random();

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay + jitter);
  }

  /** Start heartbeat interval */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(
      () => this.sendHeartbeat(),
      this.config.heartbeatInterval,
    );
  }

  /** Stop heartbeat interval */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /** Clear all timers */
  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Destroy the client */
  destroy(): void {
    this.disconnect();
    this.eventHandlers.clear();
    this.statusHandlers.clear();
  }
}

/** Singleton WebSocket client */
export const wsClient = new WSClient();
